import { difference, intersection } from "lodash";

import { GuidanceFileType } from "@/components/graphql/schema.graphql";

import { GuidanceFileParameters } from "../../../types";
import { InferenceStyleFragment } from "../../inference/queries.graphql";
import { DEFAULT_REFERENCE_SIMILARITY } from "../../inference-forms/constants";
import { getWeightFromSimilarity } from "../../inference-forms/utils/utils";
import type { DimensionsType, PresetType } from "../AspectRatio/types";
import { getAreAspectRatiosEqual } from "../AspectRatio/utils";
import { calculateCropForAspectRatioChange } from "../ImageEditorDialog/hooks/useImageCrop";
import { referenceMetadata, REFERENCES_METADATA } from "./constants";
import {
  ReferenceImageType,
  ReferenceMeshType,
  ReferenceType,
  ReferenceTypeEnum,
} from "./types";

interface CalculateDisabledReferenceTypesParams {
  imageTypes: ReferenceTypeEnum[];
  references: ReferenceType[];
}

// Calculates disabled reference types, assuming only 1 of each type of reference is supported.
export const calculateDisabledReferenceTypes = (
  params: CalculateDisabledReferenceTypesParams,
): Record<string, string> => {
  const { imageTypes, references } = params;
  const disabledTypes: Record<string, string> = {};

  const unsupportedByStyle = difference(
    Object.keys(REFERENCES_METADATA),
    imageTypes,
  );

  unsupportedByStyle.forEach((type) => {
    disabledTypes[type] =
      "This reference type is not supported for this style.";
  });

  const selectedTypes = references.map((ref) => ref.type);

  selectedTypes.forEach((type) => {
    disabledTypes[type] = "This reference type is already being used.";
  });

  Object.entries(REFERENCES_METADATA).forEach(([type, metadata]) => {
    if (disabledTypes[type]) {
      return;
    }

    const incompatibleWithSelected = intersection(
      selectedTypes,
      metadata.incompatibleReferenceTypes,
    );

    if (incompatibleWithSelected.length > 0) {
      const incompatibleLabel =
        referenceMetadata(incompatibleWithSelected[0])?.label ?? "Unknown";
      disabledTypes[type] =
        `${metadata.label} cannot be used together with ${incompatibleLabel} reference type`;
      return;
    }
  });

  return disabledTypes;
};

export const formatReferenceMeshForAPI = (
  mesh: ReferenceMeshType,
): GuidanceFileParameters => ({
  type: "INIT_MESH",
  fileId: mesh.driveFileId,
  url: mesh.src,
  weight: 1,
});

export const formatReferenceForAPI = (
  reference: ReferenceType,
): GuidanceFileParameters => {
  const { image } = reference;
  if (image === null) {
    throw new Error(
      "formatReferenceForAPI requires a reference with a non-null image",
    );
  }
  return {
    type: reference.type,
    fileId: image.driveFileId,
    url: image.src,
    preprocess:
      reference.preprocess ??
      referenceMetadata(reference.type)?.preprocess ??
      false,
    weight: getWeightFromSimilarity(reference.similarity ?? 0),
    transform: image.transform,
    group: reference.group,
  };
};

export const formatReferenceImageWithTypeForAPI = (
  image: ReferenceImageType,
  type: GuidanceFileType,
): GuidanceFileParameters => ({
  fileId: image.driveFileId,
  type,
  url: image.src,
  preprocess: Boolean(referenceMetadata(type)?.preprocess),
  transform: image.transform,
  weight: 1,
});

export const formatReferenceImageForReferences = (
  image?: ReferenceImageType,
): ReferenceType[] =>
  image
    ? [
        {
          type: "INIT_IMAGE" as ReferenceTypeEnum,
          image,
          similarity: DEFAULT_REFERENCE_SIMILARITY,
          preprocess: REFERENCES_METADATA.INIT_IMAGE?.preprocess ?? false,
        },
      ]
    : [];

export const getDefaultReference = (
  params: Partial<ReferenceType>,
): ReferenceType => {
  const {
    type = "INIT_IMAGE" as ReferenceTypeEnum,
    image,
    similarity = DEFAULT_REFERENCE_SIMILARITY,
    preprocess = referenceMetadata(type)?.preprocess ?? false,
  } = params;

  return {
    type,
    image: image ?? null,
    similarity,
    preprocess,
  };
};

const getAdjustedReferenceImageDimensions = (
  style: Pick<InferenceStyleFragment, "capabilities">,
  referenceImage: ReferenceImageType,
): DimensionsType => {
  const styleCustomSizeTargetWidth = style.capabilities?.customSizeTarget;

  if (!styleCustomSizeTargetWidth) {
    return [referenceImage.width, referenceImage.height];
  }

  return applyCustomSizeTarget(
    [referenceImage.width, referenceImage.height],
    styleCustomSizeTargetWidth,
  );
};

export const applyCustomSizeTarget = (
  dimensions: DimensionsType,
  targetWidth: number,
): DimensionsType => {
  const [width, height] = dimensions;
  const newHeight = (targetWidth * height) / width;

  const newHeightAdjusted = Math.floor(newHeight / 8) * 8; // Models like their values divisible by 8

  return [targetWidth, newHeightAdjusted];
};

export const getReferencesWithUnsupportedAspectRatio = (
  references: ReferenceType[],
  stylePresets: PresetType[],
) =>
  references.filter((reference) => {
    const { image } = reference;
    if (image === null) return false;
    return !stylePresets.some((stylePreset) =>
      getAreAspectRatiosEqual(stylePreset.dimensions, [
        image.width,
        image.height,
      ]),
    );
  });

export const getAspectRatioPresetsFromReferences = (
  references: ReferenceType[],
  style: Pick<InferenceStyleFragment, "capabilities">,
): PresetType[] =>
  references
    .filter(
      (reference): reference is ReferenceType & { image: ReferenceImageType } =>
        reference.image !== null,
    )
    .map((reference) => {
      const adjustedImageDimensions = getAdjustedReferenceImageDimensions(
        style,
        reference.image,
      );

      return {
        label: referenceMetadata(reference.type)?.label ?? "Custom",
        aspectRatioLabel: "Custom",
        aspectRatio: reference.image.width / reference.image.height,
        dimensions: adjustedImageDimensions,
      };
    });

export const updateFrameByNewAspectRatio = (
  frame: ReferenceImageType,
  dimensions: [number, number],
): ReferenceImageType | null => {
  const eps = 0.01;
  const { width: imageWidth, height: imageHeight, transform } = frame;

  const oldAspectRatio =
    transform?.crop?.width !== undefined &&
    transform?.crop?.height !== undefined
      ? transform.crop.width / transform.crop.height
      : undefined;
  const newAspectRatio = dimensions[0] / dimensions[1];

  const isAspectRatioChanged =
    !oldAspectRatio || Math.abs(newAspectRatio - oldAspectRatio) > eps;
  if (isAspectRatioChanged) {
    const newCrop = calculateCropForAspectRatioChange(
      imageWidth,
      imageHeight,
      newAspectRatio,
    );

    if (newCrop) {
      return {
        ...frame,
        transform: {
          ...frame.transform,
          crop: newCrop,
        },
      };
    }
  }

  return null;
};
